import { Breakdown } from "@xola/ui-kit";
import clsx from "clsx";
import { isGiftPurchaseItem, isLineItemWithNoPrice, isTaxFee } from "common";
import React from "react";
import { LineItem, LineItemCode, LineItemType, Purchase, PurchaseItem } from "types";
import { AddonLineItem } from "./AddonLineItem";
import { PaymentCardInfo } from "./PaymentCardInfo";
import { PaymentMethodInfo } from "./PaymentMethodInfo";
import { getAddonColor, getColor, getPriceAndQuantity } from "./utils";
import { isDefaultDemographic } from "@/models/DemographicUnit";

export const DefaultLineItem = (props: {
    lineItem: LineItem;
    purchase: Purchase;
    item?: PurchaseItem;
    lineItemChoiceCatalogName?: string | null;
}) => {
    const { lineItem, item, lineItemChoiceCatalogName } = props;
    if (lineItem.type === LineItemType.ADDON) {
        return <AddonLineItem {...props} />;
    }

    const isItemWithNoPrice = isLineItemWithNoPrice(lineItem);

    const color = lineItem.type === LineItemType.DEMOGRAPHIC ? getAddonColor(lineItem, item) : getColor(lineItem, item);
    const lineItemName = getLineItemName(lineItem, lineItemChoiceCatalogName);

    const isFixedPriceLineItem = lineItem.code === LineItemCode.PER_OUTING_PRICE;
    const isDefaultDemographicLineItem =
        lineItem.type === LineItemType.DEMOGRAPHIC && (isDefaultDemographic(lineItem.name) || isFixedPriceLineItem);

    const isGiftLineItem = lineItem.type === LineItemType.GIFT;
    const isDiscountLineItem = [
        LineItemType.PACKAGE_DISCOUNT,
        LineItemType.AFFILIATE_DISCOUNT,
        LineItemType.PARTNER_PACKAGE_DISCOUNT,
    ].includes(lineItem.type);

    const shouldTranslateLineItem = isDefaultDemographicLineItem || isGiftLineItem || isDiscountLineItem;

    return (
        <Breakdown.Item
            secondary={getPriceAndQuantity(lineItem)}
            value={isItemWithNoPrice ? "--" : lineItem.amount}
            color={color}
            info={<PaymentCardInfo {...props} />}
            methodIcon={<PaymentMethodInfo lineItem={lineItem} />}
            data-testid="default-line-item"
            classNames={{
                ...(!shouldTranslateLineItem && { children: "no-translate" }),
                info: "no-translate",
            }}
            className={clsx(
                isTaxFee(lineItem) && !isGiftPurchaseItem(item) && !lineItem.includeInAmount && "line-through",
            )}
        >
            {lineItem.type === LineItemType.PICKUP && "Pickup - "}
            {lineItemName}
        </Breakdown.Item>
    );
};

const getLineItemName = (lineItem: LineItem, lineItemChoiceCatalogName?: string | null) => {
    const isUnitLineItemWithNoPrice =
        (lineItem.type === LineItemType.DEMOGRAPHIC || lineItem.type === LineItemType.EQUIPMENT) && !lineItem.price;
    const lineItemName = lineItem.name;
    // Show quantity postfix for per-outing purchases (e.g. "Adult × 1", "Tandem Bike × 2")
    if (isUnitLineItemWithNoPrice && lineItem.quantity) {
        return (
            <span>
                {lineItemName}&nbsp;
                <span className="no-translate">× {lineItem.quantity}</span>
            </span>
        );
    }

    if (lineItemChoiceCatalogName && !lineItemName.includes(lineItemChoiceCatalogName)) {
        return (
            <span>
                {lineItemChoiceCatalogName}: {lineItemName}
            </span>
        );
    }

    return <span>{lineItemName}</span>;
};
